import { CloseOutlined, LeftOutlined } from '@ant-design/icons';
import { useReducer, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledDrawer } from './styled';
import useUserSelfPages from './useUserSelfPages';

type State =
    | { page: 'viewProfile' }
    | { page: 'editProfile' }
    | { page: 'authenticator2FA' }
    | { page: 'changePassword' }
    | { page: 'webAuthnSettings' }
    | { page: 'activeSessions' };

type Action =
    | { type: 'goBackToViewProfile' }
    | { type: 'goToEditProfile' }
    | { type: 'goToAuthenticator' }
    | { type: 'goToChangePassword' }
    | { type: 'goToWebAuthnSettings' }
    | { type: 'goToActiveSessions' };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'goBackToViewProfile':
            return { page: 'viewProfile' };

        case 'goToEditProfile':
            return { page: 'editProfile' };

        case 'goToAuthenticator':
            return { page: 'authenticator2FA' };

        case 'goToChangePassword':
            return { page: 'changePassword' };

        case 'goToWebAuthnSettings':
            return { page: 'webAuthnSettings' };

        case 'goToActiveSessions':
            return { page: 'activeSessions' };
        default:
            return state;
    }
};

export type PageTypes = State['page'];

export type UserSelfPageData = {
    type: PageTypes;
    title: React.ReactNode;
    body: React.ReactNode;
    onMenuItemClicked: () => void;
    onCloseOrBackClicked: () => void;
};

export type ActionMethods = {
    closeDrawer: () => void;
    goBackToViewProfile: () => void;
    goToEditProfile: () => void;
    goToAuthenticator: () => void;
    goToChangePassword: () => void;
    goToWebAuthnSettings: () => void;
    goToActiveSessions: () => void;
};

type UserSelfDrawerProps = {
    collapsed: boolean;
    onClose: () => void;
};

const UserSelfDrawer = ({ collapsed, onClose }: UserSelfDrawerProps) => {
    const { t } = useTranslation(['core']);
    const direction = t('core:orientation');

    const [state, dispatch] = useReducer(reducer, { page: 'viewProfile' });

    const actions = useMemo(
        () => ({
            closeDrawer: () => onClose(),
            goBackToViewProfile: () => dispatch({ type: 'goBackToViewProfile' }),
            goToEditProfile: () => dispatch({ type: 'goToEditProfile' }),
            goToAuthenticator: () => dispatch({ type: 'goToAuthenticator' }),
            goToChangePassword: () => dispatch({ type: 'goToChangePassword' }),
            goToWebAuthnSettings: () => dispatch({ type: 'goToWebAuthnSettings' }),
            goToActiveSessions: () => dispatch({ type: 'goToActiveSessions' }),
        }),
        [dispatch, onClose]
    );

    const handleMenuItemClicked = useCallback(
        (pageType: PageTypes) => {
            switch (pageType) {
                case 'viewProfile':
                    actions.goBackToViewProfile();
                    break;

                case 'editProfile':
                    actions.goToEditProfile();
                    break;

                case 'authenticator2FA':
                    actions.goToAuthenticator();
                    break;

                case 'changePassword':
                    actions.goToChangePassword();
                    break;

                case 'webAuthnSettings':
                    actions.goToWebAuthnSettings();
                    break;

                case 'activeSessions':
                    actions.goToActiveSessions();
                    break;

                default:
                    throw new Error('Page not implemented');
            }
        },
        [actions]
    );

    const pages = useUserSelfPages({ actions, onMenuItemClicked: handleMenuItemClicked });
    const currentPage = pages.find(page => page.type === state.page);

    const handleCloseOrBack = useCallback(() => {
        currentPage.onCloseOrBackClicked();
    }, [currentPage]);

    return (
        <StyledDrawer
            closeIcon={state.page === 'viewProfile' ? <CloseOutlined color="#000" /> : <LeftOutlined color="#000" />}
            onClose={handleCloseOrBack}
            open={!collapsed}
            placement={direction === 'rtl' ? 'left' : 'right'}
            title={currentPage.title}
        >
            {currentPage.body}
        </StyledDrawer>
    );
};

export default UserSelfDrawer;
